import { Capacitor, registerPlugin } from '@capacitor/core'

export interface NativeKktFiscalData {
  fiscalDocumentNumber: number
  fiscalDocumentSign: string
  fiscalDriveNumber?: string
  shiftNumber: number
  receiptNumber?: number
  dateTime?: string
}

export interface NativeKktMarkingLicense {
  number?: number
  entered?: boolean
  valid?: boolean
  validFrom?: string
  validUntil?: string
  name?: string
  error?: string
}

export interface NativeKktStatus {
  connected: boolean
  model?: string
  serial?: string
  firmware?: string
  interface?: string
  shiftOpen?: boolean
  shiftNumber?: number
  paper?: 'ok' | 'low' | 'out' | 'unknown'
  coverOpen?: boolean
  blocked?: boolean
  lastDocNumber?: number
  /** Количество чеков в открытой смене (если отдаёт драйвер). */
  receiptNumber?: number
  ffdVersion?: string
  esmEnabled?: boolean
  markingSupport?: boolean
  markingLicense?: NativeKktMarkingLicense
  fn?: {
    number?: string
    validTo?: string
    daysLeft?: number
    state?: string
  }
  ofd?: {
    unsentDocs?: number
    lastSentAt?: string
    lastError?: string
  }
  errors: Array<{ code: string; text: string; fatal?: boolean }>
  lastError?: string
  state: 'ok' | 'paper_out' | 'shift_closed' | 'error' | 'no_device'
  error?: string
}

export interface NativeKktDocument {
  fiscalDocumentNumber: number
  shiftNumber?: number
  fiscalDocumentSign?: string
  documentType?: string
  total?: number
}

export interface NativeKktReadDocumentsResult {
  success: boolean
  documents?: NativeKktDocument[]
  errors?: Array<{ fiscalDocumentNumber?: number; error?: string }>
  error?: string
}

export interface NativeKktPrintResult {
  success: boolean
  fiscalData?: NativeKktFiscalData
  error?: string
  errorCode?: string
  notPrinted?: boolean
}

export interface NativeKktSelfTestCheck {
  id: string
  label: string
  status: 'ok' | 'warn' | 'fail' | 'skip'
  detail?: string
}

interface KktPlugin {
  status(): Promise<NativeKktStatus>
  printReceipt(options: { task: string; uuid: string }): Promise<NativeKktPrintResult>
  testPrint(): Promise<{ success: boolean; error?: string }>
  openShift(): Promise<{ success: boolean; shiftNumber?: number; error?: string }>
  closeShift(): Promise<{ success: boolean; zReportNumber?: number; error?: string }>
  printXReport(): Promise<{ success: boolean; error?: string }>
  readFnDocuments(options: { from: number; to: number }): Promise<NativeKktReadDocumentsResult>
  selfTest(): Promise<{ checks: NativeKktSelfTestCheck[] }>
}

const Kkt = registerPlugin<KktPlugin>('Kkt')

/** Native-драйвер доступен только в Android-сборке. */
export function isNativeKkt(): boolean {
  return Capacitor.getPlatform() === 'android'
}

export function nativeKktStatus(): Promise<NativeKktStatus> {
  return Kkt.status()
}

export function nativeKktPrintReceipt(
  task: string,
  uuid: string
): Promise<NativeKktPrintResult> {
  return Kkt.printReceipt({ task, uuid })
}

export function nativeKktTestPrint(): Promise<{ success: boolean; error?: string }> {
  return Kkt.testPrint()
}

export function nativeKktOpenShift(): Promise<{
  success: boolean
  shiftNumber?: number
  error?: string
}> {
  return Kkt.openShift()
}

export function nativeKktCloseShift(): Promise<{
  success: boolean
  zReportNumber?: number
  error?: string
}> {
  return Kkt.closeShift()
}

export function nativeKktPrintXReport(): Promise<{ success: boolean; error?: string }> {
  return Kkt.printXReport()
}

export function nativeKktReadFnDocuments(
  from: number,
  to: number
): Promise<NativeKktReadDocumentsResult> {
  return Kkt.readFnDocuments({ from, to })
}

export function nativeKktSelfTest(): Promise<{ checks: NativeKktSelfTestCheck[] }> {
  return Kkt.selfTest()
}
